import { MobileNavigationProvider, useMobileNavigation } from "@/context/MobileNavigationContext";
import "@/App.css";
import "@/brand.css";
import ReferralCapture from "@/components/ReferralCapture";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import AskProblem from "@/pages/AskProblem";
import ExpertList from "@/pages/ExpertList";
import ExpertProfile from "@/pages/ExpertProfile";
import Booking from "@/pages/Booking";
import ConsultationRoom from "@/pages/ConsultationRoom";
import VideoConsultation from "@/pages/VideoConsultation";
import Matches from "@/pages/Matches";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerProfile from "@/pages/CustomerProfile";
import CustomerSettings from "@/pages/CustomerSettings";
import CustomerRequestDetail from "@/pages/CustomerRequestDetail";
import ExpertDashboard from "@/pages/ExpertDashboard";
import ExpertOnboarding from "@/pages/ExpertOnboarding";
import ExpertApprovalPending from "@/pages/ExpertApprovalPending";
import PendingExpertProfile from "@/pages/PendingExpertProfile";
import ExpertAccountSettings from "@/pages/ExpertAccountSettings";
import ExpertProblemDetail from "@/pages/ExpertProblemDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBusinessBrain from "@/pages/AdminBusinessBrain";
import BecomeExpert from "@/pages/BecomeExpert";
import CategoriesPage from "@/pages/CategoriesPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MobileCTA from "@/components/MobileCTA";
import Messages from "@/pages/Messages";
import { Toaster } from "@/components/ui/sonner";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";
import { SeoPolicy } from "@/components/Seo";
import BlogList from "@/pages/BlogList";
import BlogArticle from "@/pages/BlogArticle";
import TrustPage from "@/pages/TrustPage";
import NotFound from "@/pages/NotFound";
import LegalPage from "@/pages/LegalPage";
import HelpPage from "@/pages/HelpPage";
import CookieConsent from "@/components/CookieConsent";
import OperationalRules from "@/pages/OperationalRules";
import SupportAssistant from "@/components/SupportAssistant";
import SeoLandingPage from "@/pages/SeoLandingPage";

function Shell({ children }) {
  const { open: mobileMenuOpen } = useMobileNavigation();
  const { pathname } = useLocation();
  return (
    <div className={`brand-shell flex min-h-screen w-full min-w-0 max-w-full flex-col ${pathname === "/" ? "home-shell" : "interior-page"}`}>
      <Header />
      <main inert={mobileMenuOpen} className="min-w-0 max-w-full flex-1 pb-24 md:pb-0">{children}</main>
      <Footer inert={mobileMenuOpen} />
      <MobileCTA />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MobileNavigationProvider>
        <ReferralCapture />
        <AnalyticsRouteTracker />
        <SeoPolicy />
        <Shell>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/beskriv-problem" element={<AskProblem />} />
            <Route path="/eksperter" element={<ExpertList />} />
            <Route path="/kategorier" element={<CategoriesPage />} />
            <Route path="/kategorier/:slug" element={<ExpertList />} />
            <Route path="/eksperter/:id" element={<ExpertProfile />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/om-spoergmig" element={<TrustPage page="about" />} />
            <Route path="/ekspertgodkendelse" element={<TrustPage page="verification" />} />
            <Route path="/sikkerhed" element={<TrustPage page="safety" />} />
            <Route path="/privatliv" element={<LegalPage page="privacy" />} />
            <Route path="/vilkaar" element={<LegalPage page="terms" />} />
            <Route path="/cookies" element={<LegalPage page="cookies" />} />
            <Route path="/kontakt" element={<LegalPage page="contact" />} />
            <Route path="/hjaelp" element={<HelpPage />} />
            <Route path="/hjaelp/:slug" element={<SeoLandingPage />} />
            <Route path="/betaling-afbud-og-moedetid" element={<OperationalRules audience="customer" />} />
            <Route path="/regler-for-opgaver-og-betaling" element={<OperationalRules audience="expert" />} />
            <Route path="/matches/:problemId" element={<Matches />} />
            <Route path="/book/:expertId" element={<ProtectedRoute><Booking /></ProtectedRoute>} />
            <Route path="/konsultation/:bookingId" element={<ProtectedRoute><ConsultationRoom /></ProtectedRoute>} />
            <Route path="/videosamtale/:pid" element={<ProtectedRoute roles={["customer", "expert"]}><VideoConsultation /></ProtectedRoute>} />
            <Route path="/log-ind" element={<Login />} />
            <Route path="/opret-bruger" element={<Register />} />
            <Route path="/glemt-adgangskode" element={<ForgotPassword />} />
            <Route path="/nulstil-adgangskode" element={<ResetPassword />} />
            <Route path="/bekraeft-email" element={<VerifyEmail />} />
            <Route path="/tjen-penge-paa-din-viden" element={<BecomeExpert />} />
            <Route path="/bliv-ekspert" element={<BecomeExpert />} />
            <Route path="/dashboard" element={<ProtectedRoute roles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
            <Route path="/profil" element={<ProtectedRoute roles={["customer"]}><CustomerProfile /></ProtectedRoute>} />
            <Route path="/indstillinger" element={<ProtectedRoute roles={["customer"]}><CustomerSettings /></ProtectedRoute>} />
            <Route path="/beskeder" element={<ProtectedRoute roles={["customer", "expert"]}><Messages /></ProtectedRoute>} />
            <Route path="/forespoergsler/:pid" element={<ProtectedRoute roles={["customer"]}><CustomerRequestDetail /></ProtectedRoute>} />
            <Route path="/expert/onboarding" element={<ProtectedRoute roles={["expert"]} allowUnapprovedExpert><ExpertOnboarding /></ProtectedRoute>} />
            <Route path="/expert/afventer-godkendelse" element={<ProtectedRoute roles={["expert"]} allowUnapprovedExpert><ExpertApprovalPending /></ProtectedRoute>} />
            <Route path="/expert/min-profil" element={<ProtectedRoute roles={["expert"]} allowUnapprovedExpert><PendingExpertProfile /></ProtectedRoute>} />
            <Route path="/expert/indstillinger" element={<ProtectedRoute roles={["expert"]} allowUnapprovedExpert><ExpertAccountSettings /></ProtectedRoute>} />
            <Route path="/expert" element={<ProtectedRoute roles={["expert"]}><ExpertDashboard /></ProtectedRoute>} />
            <Route path="/expert/forespoergsel/:pid" element={<ProtectedRoute roles={["expert"]}><ExpertProblemDetail /></ProtectedRoute>} />
            <Route path="/admin/business-brain" element={<ProtectedRoute roles={["admin"]}><AdminBusinessBrain /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/betaling/succes" element={<PaymentSuccess />} />
            <Route path="/betaling/annulleret" element={<PaymentCancel />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Shell>
        <SupportAssistant />
        <CookieConsent />
        <Toaster />
      </MobileNavigationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
